package com.klantenstop.web.utils

import com.materialkobweb.utils.Logger
import kotlinx.browser.document
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLScriptElement

/**
 * Central ad bootstrap manager.
 *
 * Phase 1 scope:
 * - Load AdSense only after explicit Klaro consent for the "adsense" service
 * - Force non-personalized ads by default
 * - Provide a single place to trigger ad renders from UI components
 */
object AdManager {

    private const val ADSENSE_SCRIPT_ID = "ks-adsense-script"

    private var initialized: Boolean = false
    private var consentGranted: Boolean = false
    private var scriptInjected: Boolean = false
    private var adsenseClientId: String = ""
    private var nonPersonalizedOnly: Boolean = true
    private val _consentState = MutableStateFlow(false)
    val consentState: StateFlow<Boolean> = _consentState.asStateFlow()

    private val isEnabled: Boolean
        get() = initialized && consentGranted && scriptInjected

    fun initialize(clientId: String, nonPersonalizedOnly: Boolean = true) {
        val trimmedClientId = clientId.trim()

        if (trimmedClientId.isBlank()) {
            Logger.info("AdManager", "AdSense client id not configured - ads disabled")
            return
        }

        if (initialized) return
        initialized = true

        this.adsenseClientId = trimmedClientId
        this.nonPersonalizedOnly = nonPersonalizedOnly

        // Register consent callback only when config is valid
        KlaroConsentManager.onConsentChanged(ADSENSE_CONSENT_KEY) { granted ->
            setConsent(granted)
        }
    }

    fun setConsent(granted: Boolean) {
        if (!initialized || adsenseClientId.isBlank() || granted == consentGranted) return

        consentGranted = granted
        _consentState.value = granted
        if (granted) {
            applyAdPersonalizationPolicy()
            ensureScriptInjected()
            Logger.info("AdManager", "Ad consent granted - AdSense enabled")
        } else {
            Logger.info("AdManager", "Ad consent revoked - stop requesting new ads")
        }
    }

    fun mountAdSlot(
        containerId: String,
        slotName: String,
        adUnitId: String,
        adFormat: String = "auto",
        fullWidthResponsive: Boolean = true
    ) {
        if (!isEnabled) {
            trackSlotEvent(
                name = AnalyticsManager.Events.AD_SLOT_RENDER_SKIPPED,
                slotName = slotName,
                adUnitId = adUnitId,
                reason = if (!consentGranted) "no_consent" else "ads_not_initialized"
            )
            return
        }

        val container = document.getElementById(containerId) as? HTMLElement
        if (container == null) {
            trackSlotEvent(
                name = AnalyticsManager.Events.AD_SLOT_RENDER_FAILED,
                slotName = slotName,
                adUnitId = adUnitId,
                reason = "container_not_found"
            )
            Logger.warn("AdManager", "Could not find ad container: $containerId")
            return
        }

        container.innerHTML = ""

        val ins = document.createElement("ins") as? HTMLElement
        if (ins == null) {
            trackSlotEvent(
                name = AnalyticsManager.Events.AD_SLOT_RENDER_FAILED,
                slotName = slotName,
                adUnitId = adUnitId,
                reason = "ins_creation_failed"
            )
            Logger.warn("AdManager", "Could not create <ins> element for ad slot $slotName")
            return
        }

        ins.className = "adsbygoogle"
        ins.style.display = "block"
        ins.setAttribute("data-ad-client", adsenseClientId)
        ins.setAttribute("data-ad-slot", adUnitId)
        ins.setAttribute("data-ad-format", adFormat)
        ins.setAttribute("data-full-width-responsive", fullWidthResponsive.toString())
        container.appendChild(ins)

        requestAdRender(slotName = slotName, adUnitId = adUnitId)
    }

    fun requestAdRender(slotName: String, adUnitId: String) {
        if (!isEnabled) {
            trackSlotEvent(
                name = AnalyticsManager.Events.AD_SLOT_RENDER_SKIPPED,
                slotName = slotName,
                adUnitId = adUnitId,
                reason = if (!consentGranted) "no_consent" else "ads_not_initialized"
            )
            return
        }

        try {
            js("(window.adsbygoogle = window.adsbygoogle || []).push({});")
            trackSlotEvent(
                name = AnalyticsManager.Events.AD_SLOT_RENDER_REQUESTED,
                slotName = slotName,
                adUnitId = adUnitId
            )
        } catch (e: Throwable) {
            trackSlotEvent(
                name = AnalyticsManager.Events.AD_SLOT_RENDER_FAILED,
                slotName = slotName,
                adUnitId = adUnitId,
                reason = "adsbygoogle_push_failed"
            )
            Logger.error("AdManager", "Failed to request ad render:", e)
        }
    }

    private fun trackSlotEvent(
        name: String,
        slotName: String,
        adUnitId: String,
        reason: String? = null
    ) {
        AnalyticsManager.trackEvent(
            name = name,
            properties = buildMap {
                put("slot_name", slotName)
                put("ad_unit_id", adUnitId)
                put("non_personalized_only", nonPersonalizedOnly)
                if (!reason.isNullOrBlank()) put("reason", reason)
            }
        )
    }

    private fun applyAdPersonalizationPolicy() {
        if (!nonPersonalizedOnly) return

        try {
            js("window.adsbygoogle = window.adsbygoogle || [];")
            js("window.adsbygoogle.requestNonPersonalizedAds = 1;")
            Logger.info("AdManager", "Using non-personalized ads")
        } catch (e: Throwable) {
            Logger.warn("AdManager", "Could not apply non-personalized ads flag:", e)
        }
    }

    private fun ensureScriptInjected() {
        if (scriptInjected) return

        if (document.getElementById(ADSENSE_SCRIPT_ID) != null) {
            scriptInjected = true
            return
        }

        val script = (document.createElement("script") as? HTMLScriptElement) ?: run {
            Logger.error("AdManager", "Could not create script element for AdSense")
            return
        }

        script.id = ADSENSE_SCRIPT_ID
        script.async = true
        script.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=$adsenseClientId"
        script.crossOrigin = "anonymous"

        document.head?.appendChild(script)
        scriptInjected = true
    }
}



